import React from 'react';
import { Box, Typography } from '@mui/material';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { formatMonedaCompacta } from '../state/format';
import { serieSparkline } from '../state/metrics';
import { EstadoBolsa } from '../state/bolsaMachine';
import { BolsaCentro, MetricasCentro } from '../types';
import { Sparkline } from './Sparkline';
import { ProgressRing } from './ProgressRing';
import { UrgencyDots } from './UrgencyDots';

interface KpiStripProps {
  metricas: MetricasCentro | null;
  cotizaciones: BolsaCentro[];
}

const cardSx = {
  backgroundColor: '#fff',
  borderRadius: '18px',
  padding: '20px 22px',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,.65), 0 2px 4px rgba(46,36,48,.06), 0 10px 24px rgba(46,36,48,.10)',
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'space-between',
  minHeight: 108,
};

export const KpiStrip: React.FC<KpiStripProps> = ({ metricas, cotizaciones }) => {
  const porVencerBolsas = cotizaciones.filter(
    (b) => b.estado_codigo === EstadoBolsa.ENVIADA && b.validez,
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: '16px',
        marginBottom: '32px',
      }}
      data-testid="kpi-strip"
    >
      {/* Pipeline activo */}
      <Box sx={cardSx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(29), fontWeight: 700, letterSpacing: '-1px', color: TOKENS.ink }}>
              {formatMonedaCompacta(metricas?.pipeline_activo ?? 0)}
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12), color: TOKENS.muted }}>
              Pipeline activo
            </Typography>
          </Box>
          <Sparkline values={serieSparkline(cotizaciones, 'cotizado')} />
        </Box>
      </Box>

      {/* Ganado */}
      <Box sx={cardSx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(29), fontWeight: 300, letterSpacing: '-1px', color: TOKENS.ink }}>
              {formatMonedaCompacta(metricas?.ganado ?? 0)}
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12), color: TOKENS.green }}>
              ▲ {metricas?.por_estado?.[EstadoBolsa.GANADA]?.count ?? 0} cotizaciones
            </Typography>
          </Box>
          <Sparkline values={serieSparkline(cotizaciones, 'ganado')} color={TOKENS.green} />
        </Box>
      </Box>

      {/* Conversión */}
      <Box sx={{ ...cardSx, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(29), fontWeight: 300, letterSpacing: '-1px', color: TOKENS.ink }}>
            {Math.round((metricas?.conversion ?? 0) * 100)}%
          </Typography>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12), color: TOKENS.muted }}>
            sobre decididas
          </Typography>
        </Box>
        <ProgressRing porcentaje={metricas?.conversion ?? 0} />
      </Box>

      {/* Por vencer */}
      <Box sx={cardSx}>
        <Box>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(29), fontWeight: 300, letterSpacing: '-1px', color: TOKENS.ink }}>
            {metricas?.por_vencer ?? 0}
          </Typography>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12), color: TOKENS.amber }}>
            gestionar esta semana
          </Typography>
        </Box>
        <Box sx={{ marginTop: '8px' }}>
          <UrgencyDots bolsas={porVencerBolsas} />
        </Box>
      </Box>
    </Box>
  );
};
